import DeliveryFeeComparison from 'src/components/invoices/DeliveryFeeComparison'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Input,
  Modal,
  Drawer,
  Select,
  Spin,
  Tooltip,
  message,
} from 'antd'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { useCanAccess } from 'src/hooks/useIsBlocked'
import {
  getInvoiceDiscrepancy,
  requestDiscrepancyCredit,
} from 'src/utils/invoiceCreditDiscrepancyApi'
import { acceptInvoiceDifference } from 'src/features/NewInvoicesSlice'
import { isCreditNoteDocument } from 'src/utils/creditNoteDocumentHelpers'
import { canEditGoodsReceiptFromReview } from 'src/utils/invoiceUploadHelpers'
import Receipting from 'src/screens/receipting/Receipting'
import {
  CREDIT_REASONS,
  acceptDifferenceBlockReason,
  creditMoney,
  describeSplitShares,
  describeSupplierMismatchWarning,
  creditRequestBlockReason,
  shouldShowInvoiceDiscrepancyReview,
  validateCreditAmount,
} from 'src/utils/invoiceCreditDiscrepancy'
import {
  selectVatPreference,
  VAT_PREFERENCE,
} from 'src/features/VatPreferenceSlice'

const ACCEPT_CONFIRM_TITLE = 'Accept this difference?'
const ACCEPT_CONFIRM_CONTENT =
  'The order and invoice do not fully match. Accepting will mark this as reconciled without requesting a credit. The difference will remain visible in the audit history.'

/** Fetch only when opened; never infer received totals from an ordered-value match. */
export default function InvoiceDiscrepancyAction({
  invoice,
  venueId,
  onChanged,
  disabled = false,
  /** Compact stacked layout for the Matching table column. */
  compact = false,
  onEditGoodsReceipt,
}) {
  const vatPreference = useSelector(selectVatPreference)
  const canSeeInvoices = useCanAccess('invoices')
  const canSeeCredits = useCanAccess('credit-notes')
  const [open, setOpen] = useState(false)
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [reason, setReason] = useState('incorrect_price')
  const [notes, setNotes] = useState('')
  const [amount, setAmount] = useState('')
  const [editingReceipt, setEditingReceipt] = useState(false)
  const generation = useRef(0)
  const submitting = useRef(false)
  const invoiceId = invoice?.id || invoice?._id

  const load = useCallback(async () => {
    const current = ++generation.current
    setLoading(true)
    setError('')
    try {
      const result = await getInvoiceDiscrepancy(invoiceId)
      if (current !== generation.current) return
      setSummary(result)
      setAmount(
        result.comparison?.overInvoiced
          ? (result.comparison.differencePennies / 100).toFixed(2)
          : '',
      )
    } catch (err) {
      if (current === generation.current) setError(err.message)
    } finally {
      if (current === generation.current) setLoading(false)
    }
  }, [invoiceId])

  useEffect(() => {
    if (open) {
      setSummary(null)
      setSubmitted(false)
      setAccepted(false)
      setReason('incorrect_price')
      setNotes('')
      void load()
    }
    return () => {
      generation.current += 1
    }
    // Each opening must load this invoice's current financial position.
  }, [open, invoiceId, load])

  if (
    !invoiceId ||
    !canSeeInvoices ||
    invoice?.isArchived ||
    isCreditNoteDocument(invoice)
  )
    return null
  if (
    compact &&
    !open &&
    !editingReceipt &&
    !shouldShowInvoiceDiscrepancyReview(invoice)
  ) {
    return null
  }

  const comparison = summary?.comparison
  const request = summary?.creditRequest
  const blocked = creditRequestBlockReason(invoice, summary, canSeeCredits)
  const acceptBlocked = acceptDifferenceBlockReason(
    invoice,
    summary,
    canSeeInvoices,
  )
  const amountError = validateCreditAmount(
    amount,
    comparison?.differencePennies,
  )
  const remaining = summary?.netPositionPennies
  const stored = summary?.creditRequest || invoice.creditDiscrepancy
  const acceptance =
    summary?.differenceAcceptance || invoice.differenceAcceptance
  const reviewLabel =
    acceptance?.acceptedAt || stored?.amountRequestedPennies > 0
      ? 'View difference'
      : 'Review difference'
  const acceptanceTooltip = acceptance?.acceptedAt
    ? [
        'Difference accepted',
        acceptance.acceptedByName ? `by ${acceptance.acceptedByName}` : null,
        acceptance.acceptedAt
          ? new Date(acceptance.acceptedAt).toLocaleString('en-GB')
          : null,
        acceptance.differencePennies != null
          ? creditMoney(acceptance.differencePennies)
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : ''

  const close = () => {
    if (saving || accepting) return
    setOpen(false)
    // GET refreshes credit state too. Keep confirmation visible until closed.
    if (onChanged)
      Promise.resolve()
        .then(onChanged)
        .catch(() => {})
  }

  const submit = async () => {
    if (blocked || amountError || submitting.current || submitted) return
    submitting.current = true
    setSaving(true)
    setError('')
    try {
      const result = await requestDiscrepancyCredit(invoiceId, {
        reason,
        notes: notes.trim(),
        amountPennies: Math.round(Number(amount) * 100),
      })
      setSubmitted(true)
      setSummary(previous => ({
        ...previous,
        creditRequest: result.creditRequest,
      }))
      // Refresh errors must never turn a successful request into a retryable submission.
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      submitting.current = false
      setSaving(false)
    }
  }

  const runAcceptDifference = async () => {
    if (acceptBlocked || submitting.current || accepted) return
    submitting.current = true
    setAccepting(true)
    setError('')
    try {
      await acceptInvoiceDifference(invoiceId)
      setAccepted(true)
      message.success(
        'Difference accepted — invoice reconciled without a credit request',
      )
      if (onChanged) {
        await Promise.resolve()
          .then(onChanged)
          .catch(() => {})
      }
      setOpen(false)
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          'Could not accept this difference.',
      )
    } finally {
      submitting.current = false
      setAccepting(false)
    }
  }

  const confirmAcceptDifference = () => {
    if (acceptBlocked || accepting || saving || accepted) return
    Modal.confirm({
      title: ACCEPT_CONFIRM_TITLE,
      content: ACCEPT_CONFIRM_CONTENT,
      okText: 'Accept & Reconcile',
      cancelText: 'Cancel',
      okButtonProps: {
        style: { background: '#7cb305', borderColor: '#7cb305' },
      },
      onOk: () => runAcceptDifference(),
    })
  }

  const busy = saving || accepting

  const statusChip = (() => {
    if (acceptance?.acceptedAt) {
      return (
        <Tooltip title={acceptanceTooltip}>
          <AcceptedChip>
            Accepted
            {acceptance.differencePennies != null
              ? ` ${creditMoney(acceptance.differencePennies)}`
              : ''}
          </AcceptedChip>
        </Tooltip>
      )
    }
    if (stored?.amountRequestedPennies > 0) {
      const creditTitle =
        stored.status === 'resolved'
          ? `${creditMoney(
              stored.amountRequestedPennies,
            )} credit requested — credit received; review net difference`
          : `${creditMoney(
              stored.amountRequestedPennies,
            )} credit requested — ${creditMoney(
              stored.outstandingPennies,
            )} awaiting credit`
      return (
        <Tooltip title={creditTitle}>
          <CreditChip>
            {creditMoney(stored.amountRequestedPennies)} credit
            {stored.outstandingPennies > 0 ? ' open' : ''}
          </CreditChip>
        </Tooltip>
      )
    }
    return null
  })()

  return (
    <>
      {compact ? (
        <CompactTrigger>
          {statusChip}
          <CompactReviewLink
            type='link'
            size='small'
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            {reviewLabel}
          </CompactReviewLink>
        </CompactTrigger>
      ) : (
        <>
          {statusChip}
          <Button
            size='small'
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            {reviewLabel}
          </Button>
        </>
      )}
      <ResponsiveModal
        title='Invoice discrepancy'
        visible={open}
        width={640}
        onCancel={event => {
          if (editingReceipt) return
          if (
            event?.target &&
            typeof event.target.closest === 'function' &&
            event.target.closest('.ant-drawer')
          ) {
            return
          }
          close()
        }}
        maskClosable={!busy}
        closable={!busy}
        footer={
          <Actions>
            <Button disabled={busy} onClick={close}>
              Close
            </Button>
            <Button loading={loading} disabled={busy} onClick={load}>
              Refresh credits
            </Button>
            {!acceptBlocked && !accepted && (
              <Button
                loading={accepting}
                disabled={loading || busy || Boolean(error && !comparison)}
                onClick={confirmAcceptDifference}
              >
                Accept & Reconcile
              </Button>
            )}
            {!blocked && !submitted && (
              <Button
                type='primary'
                loading={saving}
                disabled={
                  loading || busy || Boolean(error) || Boolean(amountError)
                }
                onClick={submit}
              >
                Request {creditMoney(Math.round(Number(amount) * 100))} credit
              </Button>
            )}
          </Actions>
        }
      >
        <Content>
          <p>
            Invoice {invoice.invoiceNumber || '—'}
            {(summary?.allocationSlices || []).length >= 2
              ? ` · ${describeSplitShares(summary.allocationSlices)}`
              : ` · PO ${invoice.purchaseOrderNumber || '—'}`}
          </p>
          {canEditGoodsReceiptFromReview(invoice) ? (
            <Button
              style={{ marginBottom: 12 }}
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                if (onEditGoodsReceipt) {
                  onEditGoodsReceipt(invoice)
                  return
                }
                setEditingReceipt(true)
              }}
            >
              Edit Goods Receipt
            </Button>
          ) : null}
          {error && <Alert type='error' showIcon message={error} />}
          {submitted && (
            <Alert
              type='success'
              showIcon
              message='Credit request recorded. Awaiting supplier credit note.'
            />
          )}
          {accepted && (
            <Alert
              type='success'
              showIcon
              message='Difference accepted. Invoice marked reconciled — no credit request was raised.'
            />
          )}
          {loading ? (
            <Spin />
          ) : (
            summary && (
              <>
                {describeSupplierMismatchWarning(summary.supplierMismatch) ? (
                  <Alert
                    type='warning'
                    showIcon
                    message={describeSupplierMismatchWarning(
                      summary.supplierMismatch,
                    )}
                  />
                ) : null}
                <Figures>
                  {Object.keys(summary.orderedByPurchaseOrder || {}).length ? (
                    Object.entries(summary.orderedByPurchaseOrder).map(
                      ([purchaseOrderNumber, ordered]) => (
                        <div key={purchaseOrderNumber}>
                          <span>
                            Ordered
                            {Object.keys(summary.orderedByPurchaseOrder)
                              .length > 1
                              ? ` · ${purchaseOrderNumber}`
                              : ''}
                          </span>
                          <strong>
                            {creditMoney(
                              vatPreference === VAT_PREFERENCE.INC
                                ? ordered?.incPennies
                                : ordered?.exPennies,
                            )}
                          </strong>
                        </div>
                      ),
                    )
                  ) : (
                    <div>
                      <span>Ordered</span>
                      <strong>—</strong>
                      <small>See the original order in Goods receipting.</small>
                    </div>
                  )}
                  <DeliveryFeeComparison
                    comparison={invoice.matchSummary?.deliveryComparison}
                  />
                  {invoice.matchSummary?.orderNetPennies != null ? (
                    <>
                      <div>
                        <span>
                          {invoice.matchSummary?.deliveryComparison
                            ?.expectedNetPennies > 0
                            ? 'Received total incl delivery (ex VAT)'
                            : 'Goods received (ex VAT)'}
                        </span>
                        <strong>
                          {creditMoney(invoice.matchSummary.orderNetPennies)}
                        </strong>
                      </div>
                      <div>
                        <span>Goods received VAT</span>
                        <strong>
                          {creditMoney(
                            invoice.matchSummary.orderVatPennies ?? 0,
                          )}
                        </strong>
                      </div>
                    </>
                  ) : null}
                  {(summary?.allocationSlices || []).length >= 2 ? (
                    <>
                      <div>
                        <span>Supplier invoice</span>
                        <strong>
                          {creditMoney(comparison?.invoiceTotalPennies)}
                          {' · '}
                          {describeSplitShares(summary.allocationSlices)}
                          <small>
                            {' '}
                            The total entered on the invoice.
                            {(summary.allocationSlices || []).some(
                              slice => slice.receiptSaved !== false,
                            )
                              ? ' The VAT toggle changes the receipt lines.'
                              : ''}
                          </small>
                        </strong>
                      </div>
                      {summary.allocationSlices.map(slice => (
                        <div key={slice.purchaseOrderNumber}>
                          <span>
                            {creditMoney(slice.amountPennies)} on{' '}
                            {slice.purchaseOrderNumber}
                          </span>
                          <strong>
                            {slice.receiptSaved === false
                              ? 'Not receipted yet'
                              : `Receipt ${creditMoney(
                                  vatPreference === VAT_PREFERENCE.INC
                                    ? slice.receivedIncPennies
                                    : slice.receivedExPennies,
                                )} · difference ${creditMoney(
                                  (vatPreference === VAT_PREFERENCE.INC
                                    ? slice.receivedIncPennies
                                    : slice.receivedExPennies) == null
                                    ? slice.differencePennies
                                    : slice.amountPennies -
                                        (vatPreference === VAT_PREFERENCE.INC
                                          ? slice.receivedIncPennies
                                          : slice.receivedExPennies),
                                )}`}
                          </strong>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div>
                        <span>
                          Goods received (
                          {vatPreference === VAT_PREFERENCE.INC
                            ? 'inc VAT'
                            : 'ex VAT'}
                          )
                        </span>
                        <strong>
                          {creditMoney(
                            Object.values(
                              summary.receivedDisplayByPurchaseOrder || {},
                            ).reduce(
                              (sum, row) =>
                                sum +
                                (vatPreference === VAT_PREFERENCE.INC
                                  ? Number(row?.incPennies) || 0
                                  : Number(row?.exPennies) || 0),
                              0,
                            ) || comparison?.receivedGrossPennies,
                          )}
                        </strong>
                      </div>
                      <div>
                        <span>Supplier invoice</span>
                        <strong>
                          {creditMoney(comparison?.invoiceTotalPennies)}
                          <small>
                            {' '}
                            The total entered on the invoice.
                            {Object.values(
                              summary.receivedDisplayByPurchaseOrder || {},
                            ).some(
                              row =>
                                row?.exPennies != null ||
                                row?.incPennies != null,
                            )
                              ? ' The VAT toggle changes the receipt lines.'
                              : ''}
                          </small>
                          {String(
                            invoice.matchSummary?.catchWeightPriceLabel || '',
                          ).trim()
                            ? ` · Receipted ${String(
                                invoice.matchSummary.catchWeightPriceLabel,
                              ).trim()}`
                            : ''}
                        </strong>
                      </div>
                      <div>
                        <span>Invoice minus goods received</span>
                        <strong>
                          {creditMoney(comparison?.differencePennies)}
                        </strong>
                      </div>
                    </>
                  )}
                  {(summary?.allocationSlices || []).length < 2 || request ? (
                    <div>
                      <span>Remaining invoice difference after credits</span>
                      <strong>{creditMoney(remaining)}</strong>
                    </div>
                  ) : null}
                </Figures>
                {request ? (
                  <>
                    <Alert
                      showIcon
                      type={
                        remaining != null &&
                        Math.abs(remaining) <=
                          (comparison?.tolerancePennies || 0)
                          ? 'success'
                          : 'warning'
                      }
                      message={
                        request.outstandingPennies > 0
                          ? `${creditMoney(
                              request.outstandingPennies,
                            )} credit outstanding — awaiting supplier credit note`
                          : remaining > (comparison?.tolerancePennies || 0)
                          ? `Requested credit received; ${creditMoney(
                              remaining,
                            )} invoice difference still needs review`
                          : 'Requested credit received — review the net position before reconciling'
                      }
                    />
                    <Figures>
                      <div>
                        <span>Credit requested</span>
                        <strong>
                          {creditMoney(request.amountRequestedPennies)}
                        </strong>
                      </div>
                      <div>
                        <span>Credit notes received</span>
                        <strong>
                          {creditMoney(request.creditReceivedPennies)}
                        </strong>
                      </div>
                      <div>
                        <span>Request outstanding</span>
                        <strong>
                          {creditMoney(request.outstandingPennies)}
                        </strong>
                      </div>
                    </Figures>
                    <p>
                      Reason:{' '}
                      {CREDIT_REASONS[request.reason] || request.reason || '—'}
                      <br />
                      Requested by {request.requestedByName || '—'}
                      {request.requestedAt
                        ? ` · ${new Date(request.requestedAt).toLocaleString(
                            'en-GB',
                          )}`
                        : ''}
                    </p>
                    {request.notes && (
                      <p style={{ whiteSpace: 'pre-wrap' }}>{request.notes}</p>
                    )}
                    <p>
                      {request.creditNoteIds?.length || 0} credit note(s)
                      counted. A requested credit is not money recovered.
                    </p>
                  </>
                ) : blocked ? (
                  <>
                    <Alert type='info' showIcon message={blocked} />
                    {!acceptBlocked && (
                      <Alert
                        type='warning'
                        showIcon
                        message='Satisfied with this discrepancy?'
                        description='Use Accept & Reconcile to acknowledge the difference, mark the invoice reconciled, and keep the original ordered / received / invoiced values in the audit history. No credit request is raised.'
                      />
                    )}
                  </>
                ) : (
                  <>
                    <Alert
                      type='warning'
                      showIcon
                      message={`Invoice is ${creditMoney(
                        comparison.differencePennies,
                      )} higher than the goods received`}
                    />
                    <label htmlFor={`credit-reason-${invoiceId}`}>Reason</label>
                    <Select
                      id={`credit-reason-${invoiceId}`}
                      value={reason}
                      disabled={busy}
                      onChange={setReason}
                      style={{ width: '100%' }}
                      getPopupContainer={trigger =>
                        trigger.closest('.ant-modal-body') ||
                        trigger.parentElement
                      }
                    >
                      {Object.entries(CREDIT_REASONS).map(([value, label]) => (
                        <Select.Option key={value} value={value}>
                          {label}
                        </Select.Option>
                      ))}
                    </Select>
                    <label htmlFor={`credit-notes-${invoiceId}`}>
                      Notes (optional)
                    </label>
                    <Input.TextArea
                      id={`credit-notes-${invoiceId}`}
                      value={notes}
                      disabled={busy}
                      maxLength={1000}
                      rows={3}
                      onChange={e => setNotes(e.target.value)}
                      placeholder='Explain the charge being disputed. For a specific line, include its product, quantity and price.'
                    />
                    <label htmlFor={`credit-amount-${invoiceId}`}>
                      Credit requested (£, inc VAT)
                    </label>
                    <Input
                      id={`credit-amount-${invoiceId}`}
                      inputMode='decimal'
                      value={amount}
                      disabled={busy}
                      onChange={e => setAmount(e.target.value)}
                    />
                    {amountError && <span role='alert'>{amountError}</span>}
                    <p>
                      This requests credit for the invoice. A smaller request
                      leaves the rest of the invoice difference unresolved. If
                      you are satisfied with the discrepancy, use Accept &amp;
                      Reconcile instead — values are not forced to match.
                    </p>
                  </>
                )}
              </>
            )
          )}
          <Alert
            type='info'
            message='Correct receipt, incorrect supplier charge?'
            description='Request credit. The original invoice amount and received quantities and prices stay unchanged. Reconciliation requires the actual credit note. If you do not want a credit, Accept & Reconcile keeps the difference in history.'
          />
          <p>
            Entered the wrong receipt in OMS?{' '}
            <Link to={`/v/${venueId}/purchasing/receipting`}>
              Open Goods receipting
            </Link>{' '}
            and use Edit receipt if your role permits retrospective corrections.
          </p>
          {canSeeCredits && (
            <Link to={`/v/${venueId}/purchasing/creditnotes`}>
              Open Credit Requests
            </Link>
          )}
        </Content>
      </ResponsiveModal>
      <Drawer
        title='Edit goods receipt'
        visible={editingReceipt}
        width={1040}
        destroyOnClose
        onClose={() => {
          setEditingReceipt(false)
          load()
        }}
      >
        {editingReceipt ? (
          <Receipting
            embedded
            embeddedPo={String(
              invoice.purchaseOrderNumber ||
                invoice.poAllocations?.[0]?.purchaseOrderNumber ||
                '',
            ).trim()}
            onReceiptSaved={() => {
              setEditingReceipt(false)
              load()
            }}
          />
        ) : null}
      </Drawer>
    </>
  )
}

const ResponsiveModal = styled(Modal)`
  max-width: calc(100vw - 24px);
  .ant-modal-body {
    max-height: 70vh;
    overflow-y: auto;
  }
  @media (max-width: 480px) {
    .ant-modal-body {
      padding: 16px;
    }
  }
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-wrap: anywhere;
  p {
    margin: 0;
  }
`
const Figures = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  div {
    padding: 12px;
    background: #fafafa;
    border-radius: 6px;
  }
  span,
  strong {
    display: block;
  }
  strong {
    font-size: 18px;
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  .ant-btn {
    margin-left: 0 !important;
    height: auto;
    min-height: 32px;
    white-space: normal;
  }
`
const CompactTrigger = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  max-width: 100%;
`

const CompactReviewLink = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    height: auto;
    min-height: 0;
    margin: 0;
    padding: 0;
    border: none;
    box-shadow: none;
    background: transparent;
    color: #595959;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;

    &:hover,
    &:focus {
      color: #262626;
      background: transparent;
      border: none;
      box-shadow: none;
    }
  }
`

const AcceptedChip = styled.span`
  display: inline-block;
  max-width: 140px;
  padding: 0 6px;
  border-radius: 4px;
  border: 1px solid #b7eb8f;
  background: #f6ffed;
  color: #389e0d;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CreditChip = styled.span`
  display: inline-block;
  max-width: 140px;
  padding: 0 6px;
  border-radius: 4px;
  border: 1px solid #ffe58f;
  background: #fffbe6;
  color: #874d00;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
